// File: frontend/src/components/admin/AdminSchedulers.tsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaClock,
  FaSave,
  FaCheck,
  FaSpinner,
  FaPlay,
  FaUndo,
  FaExclamationTriangle,
  FaQuestionCircle,
} from "react-icons/fa";
import {
  getSchedulerOverview,
  updateScheduler,
  runSchedulerJobNow,
  ScheduledJobDto,
  SchedulerOverviewDto,
  SchedulerJobUpdate,
} from "../../services/adminSchedulerService";
import {
  CRON_PRESETS,
  CRON_CUSTOM_ID,
  findPresetIdByCron,
  describeCron,
  looksLikeValidCron,
  cronFieldCount,
} from "../../utils/cronUtils";
import type { Translate } from "../../utils/cronUtils";
import { formatDateTime } from "../../utils/dateUtils";
import { AdminErrorBanner } from "../common/AdminBanner";
import "./AdminSchedulers.css";

/**
 * Scheduler section of the admin settings panel.
 *
 * Background jobs used to carry their timing in @Scheduled(cron = ...), so
 * changing when one runs meant editing Java and redeploying. The expressions now
 * live in system_settings and this section is where they are edited: a preset
 * dropdown for the ordinary cases, the raw cron string for everything else, an
 * on/off switch per job, and the next fire times computed by the server so the
 * effect of a change is visible before anyone waits for it.
 *
 * ADMIN only — AdminSettings does not render this for moderators, and the
 * backend rejects them independently.
 */

/** Local, unsaved edits for one job. */
interface JobDraft {
  cronExpression: string;
  enabled: boolean;
}

/** i18n key for a job's display name, falling back to the raw code. */
const jobTitleKey = (code: string): string =>
  `admin_scheduler_job_${code.toLowerCase()}_title`;

/** i18n key for the sentence explaining what a job does. */
const jobDescKey = (code: string): string =>
  `admin_scheduler_job_${code.toLowerCase()}_desc`;

/** Timezones offered in the dropdown; the stored value is added if it is not here. */
const COMMON_TIMEZONES = [
  "UTC",
  "Europe/Istanbul",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Moscow",
  "America/New_York",
];

export const AdminSchedulers: React.FC = () => {
  const { t } = useTranslation();

  const [overview, setOverview] = useState<SchedulerOverviewDto | null>(null);
  const [drafts, setDrafts]     = useState<Record<string, JobDraft>>({});
  const [timezone, setTimezone] = useState<string>("UTC");

  const [loading, setLoading]   = useState(true);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const [runningCode, setRunningCode] = useState<string | null>(null);
  const [error, setError]       = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  /** Reset local edit state from a server response. */
  const applyOverview = useCallback((data: SchedulerOverviewDto) => {
    setOverview(data);
    setTimezone(data.timezone);
    const next: Record<string, JobDraft> = {};
    data.jobs.forEach(job => {
      next[job.code] = {
        cronExpression: job.cronExpression,
        enabled: job.enabled,
      };
    });
    setDrafts(next);
  }, []);

  // Load once on mount. `t` is deliberately not a dependency — it changes
  // identity when i18n finishes loading and would trigger a second fetch.
  useEffect(() => {
    setLoading(true);
    getSchedulerOverview()
      .then(data => {
        applyOverview(data);
        setError(null);
      })
      .catch(() => setError(t("admin_scheduler_load_error")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyOverview]);

  const updateDraft = useCallback((code: string, patch: Partial<JobDraft>) => {
    setDrafts(prev => ({ ...prev, [code]: { ...prev[code], ...patch } }));
  }, []);

  /** Are there any local edits not yet sent to the server? */
  const isDirty = useMemo(() => {
    if (!overview) return false;
    if (overview.timezone !== timezone) return true;
    return overview.jobs.some(job => {
      const draft = drafts[job.code];
      if (!draft) return false;
      return draft.cronExpression.trim() !== job.cronExpression || draft.enabled !== job.enabled;
    });
  }, [overview, drafts, timezone]);

  /** Every edited expression must at least have the right shape before we send it. */
  const hasInvalidDraft = useMemo(
    () => Object.values(drafts).some(d => !looksLikeValidCron(d.cronExpression)),
    [drafts]
  );

  const handleSave = useCallback(async () => {
    if (!overview) return;

    const jobs: SchedulerJobUpdate[] = overview.jobs.map(job => ({
      code: job.code,
      cronExpression: (drafts[job.code]?.cronExpression ?? job.cronExpression).trim(),
      enabled: drafts[job.code]?.enabled ?? job.enabled,
    }));

    setSaveState("saving");
    setError(null);
    try {
      const data = await updateScheduler({ timezone, jobs });
      applyOverview(data);
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("idle");
      setError(t("admin_scheduler_save_error"));
    }
  }, [overview, drafts, timezone, applyOverview, t]);

  const handleRunNow = useCallback(
    async (code: string) => {
      setRunningCode(code);
      setError(null);
      try {
        const data = await runSchedulerJobNow(code);
        // The run is queued, not finished — re-read shortly after so the panel
        // shows the outcome instead of a permanent "RUNNING".
        applyOverview(data);
        setTimeout(() => {
          getSchedulerOverview().then(applyOverview).catch(() => undefined);
        }, 2000);
      } catch {
        setError(t("admin_scheduler_run_error"));
      } finally {
        setRunningCode(null);
      }
    },
    [applyOverview, t]
  );

  // ── Render helpers ──────────────────────────────────────────────

  const renderStatusBadge = (job: ScheduledJobDto) => {
    const status = job.running ? "RUNNING" : job.lastStatus;
    const modifier = status.toLowerCase().replace("_", "-");
    return (
      <span className={`scheduler-status scheduler-status--${modifier}`}>
        {t(`admin_scheduler_status_${status.toLowerCase()}`)}
      </span>
    );
  };

  const renderJob = (job: ScheduledJobDto) => {
    const draft   = drafts[job.code] ?? { cronExpression: job.cronExpression, enabled: job.enabled };
    const presetId = findPresetIdByCron(draft.cronExpression);
    const shapeOk  = looksLikeValidCron(draft.cronExpression);
    const fieldCount = cronFieldCount(draft.cronExpression);

    return (
      <div key={job.code} className="scheduler-job">
        {/* Job header: name, live badge, on/off */}
        <div className="scheduler-job-header">
          <div className="scheduler-job-titles">
            <h4 className="scheduler-job-title">{t(jobTitleKey(job.code))}</h4>
            <p className="scheduler-job-desc">{t(jobDescKey(job.code))}</p>
          </div>
          <div className="scheduler-job-header-right">
            {renderStatusBadge(job)}
            <label className="settings-toggle">
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={e => updateDraft(job.code, { enabled: e.target.checked })}
              />
              <span className="settings-toggle-slider" />
            </label>
          </div>
        </div>

        {/* Schedule editing: preset dropdown + raw expression */}
        <div className="scheduler-job-fields">
          <div className="settings-field">
            <label>{t("admin_scheduler_preset_label")}</label>
            <select
              value={presetId}
              onChange={e => {
                const preset = CRON_PRESETS.find(p => p.id === e.target.value);
                if (preset) updateDraft(job.code, { cronExpression: preset.cron });
              }}
            >
              {presetId === CRON_CUSTOM_ID && (
                <option value={CRON_CUSTOM_ID}>{t("admin_scheduler_preset_custom")}</option>
              )}
              {CRON_PRESETS.map(preset => (
                <option key={preset.id} value={preset.id}>
                  {t(preset.labelKey)}
                </option>
              ))}
            </select>
            <p className="settings-field-hint">{t("admin_scheduler_preset_hint")}</p>
          </div>

          <div className="settings-field">
            <label>{t("admin_scheduler_cron_label")}</label>
            <div className="scheduler-cron-row">
              <input
                type="text"
                className={`scheduler-cron-input${shapeOk ? "" : " scheduler-cron-input--invalid"}`}
                value={draft.cronExpression}
                spellCheck={false}
                onChange={e => updateDraft(job.code, { cronExpression: e.target.value })}
              />
              <button
                type="button"
                className="scheduler-icon-btn"
                title={t("admin_scheduler_reset_default", { cron: job.defaultCron })}
                onClick={() => updateDraft(job.code, { cronExpression: job.defaultCron })}
              >
                <FaUndo />
              </button>
            </div>
            {shapeOk ? (
              <p className="settings-field-hint scheduler-cron-desc">
                {/* cast: i18next's TFunction has overloads that do not line up with the plain signature cronUtils declares */}
                {describeCron(draft.cronExpression, t as unknown as Translate)}
              </p>
            ) : (
              <p className="scheduler-warning">
                <FaExclamationTriangle />{" "}
                {t("admin_scheduler_cron_shape_error", { fields: fieldCount })}
              </p>
            )}
          </div>
        </div>

        {/* Server-side truth: is it armed, when does it fire next */}
        {!job.cronValid && (
          <p className="scheduler-warning">
            <FaExclamationTriangle /> {t("admin_scheduler_saved_cron_invalid")}
          </p>
        )}

        <div className="scheduler-job-runtime">
          <div className="scheduler-runtime-block">
            <span className="scheduler-runtime-label">{t("admin_scheduler_next_runs")}</span>
            {job.nextRunTimes.length === 0 ? (
              <span className="scheduler-runtime-value scheduler-runtime-value--muted">
                {job.enabled ? t("admin_scheduler_next_runs_none") : t("admin_scheduler_disabled_note")}
              </span>
            ) : (
              <ul className="scheduler-next-list">
                {job.nextRunTimes.map(time => (
                  <li key={time}>{formatDateTime(time)}</li>
                ))}
              </ul>
            )}
          </div>

          <div className="scheduler-runtime-block">
            <span className="scheduler-runtime-label">{t("admin_scheduler_last_run")}</span>
            {job.lastStartedAt ? (
              <span className="scheduler-runtime-value">
                {formatDateTime(job.lastStartedAt)}
                {job.lastDurationMs !== null && ` · ${job.lastDurationMs} ms`}
                {job.lastRunManual && ` · ${t("admin_scheduler_manual_tag")}`}
              </span>
            ) : (
              <span className="scheduler-runtime-value scheduler-runtime-value--muted">
                {t("admin_scheduler_never_run")}
              </span>
            )}
            <span className="scheduler-runtime-value scheduler-runtime-value--muted">
              {t("admin_scheduler_totals", {
                runs: job.totalRuns,
                failures: job.totalFailures,
              })}
            </span>
            {job.lastError && <span className="scheduler-error-text">{job.lastError}</span>}
          </div>

          <div className="scheduler-runtime-actions">
            <button
              type="button"
              className="scheduler-run-btn"
              onClick={() => handleRunNow(job.code)}
              disabled={runningCode === job.code || job.running}
            >
              {runningCode === job.code ? <FaSpinner className="fa-spin" /> : <FaPlay />}
              {t("admin_scheduler_run_now")}
            </button>
            <p className="settings-field-hint">{t("admin_scheduler_run_now_hint")}</p>
          </div>
        </div>
      </div>
    );
  };

  // ── Main render ─────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="settings-group">
        <div className="settings-loading">
          <FaSpinner className="fa-spin" />
          {t("admin_scheduler_loading")}
        </div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="settings-group">
        <AdminErrorBanner message={error ?? t("admin_scheduler_load_error")} />
      </div>
    );
  }

  const timezoneOptions = COMMON_TIMEZONES.includes(timezone)
    ? COMMON_TIMEZONES
    : [timezone, ...COMMON_TIMEZONES];

  return (
    <div className="settings-group admin-schedulers">
      {/* Group header */}
      <div className="settings-group-header">
        <span className="settings-group-icon"><FaClock /></span>
        <h3 className="settings-group-title">{t("admin_settings_group_scheduler")}</h3>
        <button
          type="button"
          className="scheduler-help-toggle"
          onClick={() => setHelpOpen(open => !open)}
          title={t("admin_scheduler_help_toggle")}
        >
          <FaQuestionCircle />
        </button>
      </div>

      <div className="scheduler-body">
        <AdminErrorBanner message={error} />

        <p className="scheduler-intro">{t("admin_scheduler_intro")}</p>

        {/* Collapsible cron cheat sheet */}
        {helpOpen && (
          <div className="scheduler-help">
            <p className="scheduler-help-title">{t("admin_scheduler_help_title")}</p>
            <pre className="scheduler-help-diagram">{`┌───────── ${t("admin_scheduler_field_second")}
│ ┌─────── ${t("admin_scheduler_field_minute")}
│ │ ┌───── ${t("admin_scheduler_field_hour")}
│ │ │ ┌─── ${t("admin_scheduler_field_dom")}
│ │ │ │ ┌─ ${t("admin_scheduler_field_month")}
│ │ │ │ │ ┌ ${t("admin_scheduler_field_dow")}
│ │ │ │ │ │
0 0 3 * * *`}</pre>
            <ul className="scheduler-help-list">
              <li><code>0 * * * * *</code> — {t("admin_scheduler_preset_every_minute")}</li>
              <li><code>0 */15 * * * *</code> — {t("admin_scheduler_preset_every_15_minutes")}</li>
              <li><code>0 0 * * * *</code> — {t("admin_scheduler_preset_hourly")}</li>
              <li><code>0 0 3 * * *</code> — {t("admin_scheduler_preset_daily_3am")}</li>
              <li><code>0 0 3,15 * * *</code> — {t("admin_scheduler_preset_twice_daily")}</li>
              <li><code>0 0 3 * * MON</code> — {t("admin_scheduler_preset_weekly_monday")}</li>
              <li><code>0 0 3 1 * *</code> — {t("admin_scheduler_preset_monthly_first")}</li>
            </ul>
            <p className="scheduler-help-note">{t("admin_scheduler_help_six_fields")}</p>
          </div>
        )}

        {/* Shared timezone */}
        <div className="settings-field scheduler-timezone-field">
          <label>{t("admin_scheduler_timezone")}</label>
          <select value={timezone} onChange={e => setTimezone(e.target.value)}>
            {timezoneOptions.map(zone => (
              <option key={zone} value={zone}>{zone}</option>
            ))}
          </select>
          <p className="settings-field-hint">
            {t("admin_scheduler_timezone_hint")}{" "}
            {t("admin_scheduler_server_time", { time: formatDateTime(overview.serverTime) })}
          </p>
        </div>

        {/* One card per job */}
        {overview.jobs.map(renderJob)}
      </div>

      {/* Footer with the section save button */}
      <div className="settings-group-footer">
        {saveState === "saved" && (
          <span className="settings-save-success">
            <FaCheck /> {t("admin_settings_saved")}
          </span>
        )}
        {hasInvalidDraft && (
          <span className="scheduler-footer-warning">
            <FaExclamationTriangle /> {t("admin_scheduler_fix_before_save")}
          </span>
        )}
        <button
          className="settings-save-btn"
          onClick={handleSave}
          disabled={saveState === "saving" || hasInvalidDraft || !isDirty}
        >
          {saveState === "saving" ? (
            <>
              <FaSpinner className="fa-spin" />
              {t("admin_settings_saving")}
            </>
          ) : (
            <>
              <FaSave />
              {t("admin_settings_save_group")}
            </>
          )}
        </button>
      </div>
    </div>
  );
};